import React from 'react'
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  IconButton,
  Box,
  Text,
  Spinner,
  Flex
} from '@chakra-ui/core'
import {
  Maybe,
  NotificationEntity,
  useGetNotificationsQuery,
  useGetUnreadNotificationsCountQuery
} from '../../generated/graphql'
import moment from 'moment'
import { useAppContext } from '../../context/AppProvider'
import { Bell } from 'react-feather'
import { NotificationDetails, NotificationList } from './components'
import { InView } from 'react-intersection-observer'
import { useNotificationSocketEvents } from '../../hooks'

const DEFAULT_LIMIT = 15
const Notifications = () => {
  const [start, setStart] = React.useState(0)
  const [limit, setLimit] = React.useState(DEFAULT_LIMIT)
  const [isNotificationOpen, setIsNotificationOpen] = React.useState(false)
  const { data, refetch, loading } = useGetNotificationsQuery({
    fetchPolicy: 'network-only',
    variables: {
      pagination: {
        start,
        limit
      },
      sort: 'createdAt:desc'
    }
  })
  const {
    data: unreadNotificationCountData,
    loading: unreadLoading,
    refetch: refetchUnreadCount
  } = useGetUnreadNotificationsCountQuery({
    fetchPolicy: 'network-only'
  })
  const unreadNotificationCount = unreadNotificationCountData?.getUnreadNotificationsCount || 0
  const [selectedNotification, setSelectedNotification] = React.useState<Maybe<string>>('')
  const { isTabletOrMobile, setOnDrawer2Close } = useAppContext()

  useNotificationSocketEvents(async () => {
    await refetchUnreadCount()
    await refetch({
      pagination: {
        start: 0,
        limit
      },
      sort: 'createdAt:desc'
    })
    setStart(0)
  })

  React.useEffect(() => {
    setOnDrawer2Close(() => () => {
      if (isNotificationOpen) {
        setIsNotificationOpen(false)
      }
    })

    return () => {
      setOnDrawer2Close(null)
    }
  }, [isNotificationOpen, setOnDrawer2Close])

  const notifications = React.useMemo(
    () =>
      (data?.getDataCollectorNotifications || []).map((n: any) => ({
        id: n?.id,
        attributes: n?.attributes
      })),
    [data]
  )

  const groupedNotifications = React.useMemo(
    () =>
      notifications?.reduce((acc, notification) => {
        const date = moment(notification?.attributes?.createdAt).format('YYYY-MM-DD')
        if (!acc[date]) {
          acc[date] = []
        }
        acc[date].push(notification)
        return acc
      }, {} as Record<string, Maybe<NotificationEntity>[]>),
    [notifications]
  )
  const groupedNotificationsArray = Object.entries(groupedNotifications || {}).sort(
    ([a], [b]) => moment(b).unix() - moment(a).unix()
  )
  return (
    <Popover
      placement="bottom-end"
      closeOnBlur={false}
      isOpen={isNotificationOpen}
      onClose={() => setIsNotificationOpen(false)}
    >
      <PopoverTrigger>
        <Box position="relative" display="flex" alignItems="center">
          <IconButton
            icon={Bell}
            aria-label="Notification"
            size="sm"
            mr={2}
            fontSize="3xl"
            variant="ghost"
            variantColor="gray"
            onClick={() => setIsNotificationOpen(!isNotificationOpen)}
          />
          {unreadNotificationCount && !unreadLoading ? (
            <Box
              position="absolute"
              top={-5}
              right={-5}
              bg="red.500"
              color="white"
              borderRadius="full"
              width={5}
              height={5}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
            >
              <Text fontWeight="bold" fontSize="x-small">
                {unreadNotificationCount}
              </Text>
            </Box>
          ) : null}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        zIndex={4}
        minW={isTabletOrMobile ? '98%' : '70vh'}
        borderColor="gray.50"
        borderRadius="lg"
      >
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverBody p={0}>
          <Box maxHeight={isTabletOrMobile ? '80vh' : '70vh'} overflowY="auto" width="100%">
            {selectedNotification ? (
              <NotificationDetails
                notificationId={selectedNotification}
                onBack={async () => {
                  setSelectedNotification('')
                  await refetch()
                }}
                key={selectedNotification}
              />
            ) : (
              <>
                <NotificationList
                  groupedNotificationsArray={groupedNotificationsArray}
                  onRefetchUnread={async () => {
                    await refetchUnreadCount()
                  }}
                  onClickNotification={async (notificationId) => {
                    if (unreadNotificationCount > 0) {
                      await refetchUnreadCount()
                    }
                    setSelectedNotification(notificationId)
                  }}
                />
                {notifications.length >= limit && (
                  <InView
                    threshold={0}
                    onChange={async (inView) => {
                      if (inView) {
                        setLimit((prev) => prev + DEFAULT_LIMIT)
                      }
                    }}
                  >
                    {({ ref }) => <Box ref={ref} height={1} />}
                  </InView>
                )}
              </>
            )}
            {loading && (
              <Flex w="100%" align="center" justify="center" p={4}>
                <Spinner color="green.300" />
              </Flex>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default Notifications
